import { useEffect, useState } from "react";
import { Box, Card, CardContent, CardMedia, Container, Typography } from "@mui/material";
import SchoolIcon from "@mui/icons-material/School";
import { collection, getDocs, orderBy, query } from "firebase/firestore";
import { db } from "../../firebase";
import { useParams } from "react-router-dom";
import StripedGuide from "./components/StripedGuide";

export const HandysSchuleCollections = () => {
	const { collectionId } = useParams();
	const [collections, setCollections] = useState([]);
	const [itemsByCollection, setItemsByCollection] = useState({});
	const [isLoading, setIsLoading] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");

	useEffect(() => {
		const loadCollections = async () => {
			setIsLoading(true);
			setErrorMessage("");
			try {
				const collectionsRef = collection(db, "handys_schule_collections");
				const collectionsSnap = await getDocs(query(collectionsRef, orderBy("order", "asc")));
				const list = collectionsSnap.docs.map((docSnap) => ({
					id: docSnap.id,
					...docSnap.data()
				}));
				setCollections(list);

				const itemsEntries = await Promise.all(
					list.map(async (col) => {
						const itemsRef = collection(
							db,
							"handys_schule_collections",
							col.id,
							"items"
						);
						const itemsSnap = await getDocs(query(itemsRef, orderBy("order", "asc")));
						const items = itemsSnap.docs.map((itemSnap) => ({
							id: itemSnap.id,
							...itemSnap.data()
						}));
						return [col.id, items];
					})
				);

				setItemsByCollection(Object.fromEntries(itemsEntries));
			} catch (error) {
				console.error("Failed to load handys schule collections:", error);
				setErrorMessage("Fehler beim Laden der Sammlungen.");
			} finally {
				setIsLoading(false);
			}
		};

		void loadCollections();
	}, []);

	const visibleCollections = collectionId
		? collections.filter((collectionItem) => collectionItem.id === collectionId)
		: collections;

	return (
		<Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
			<Typography variant="h4" gutterBottom>
				Handys Schule
			</Typography>

			{errorMessage && (
				<Typography variant="body2" color="error" sx={{ mb: 2 }}>
					{errorMessage}
				</Typography>
			)}

			{isLoading && (
				<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
					Lade Sammlungen...
				</Typography>
			)}

			{!isLoading && collections.length === 0 && (
				<Typography variant="body2" color="text.secondary">
					Keine Sammlungen vorhanden.
				</Typography>
			)}

			{!isLoading && collectionId && visibleCollections.length === 0 && (
				<Typography variant="body2" color="text.secondary">
					Sammlung nicht gefunden.
				</Typography>
			)}

			<Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
				{visibleCollections.map((collectionItem) => {
					const items = itemsByCollection[collectionItem.id] || [];
					return (
						<Card key={collectionItem.id} variant="outlined">
							<CardContent>
								<Typography variant="h6" sx={{ mb: 2 }}>
									{collectionItem.name}
								</Typography>
								{items.length === 0 && (
									<Typography variant="body2" color="text.secondary">
										Keine Eintraege vorhanden.
									</Typography>
								)}
								<Box
									sx={{
										display: "grid",
										gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", md: "repeat(3, minmax(0, 1fr))" },
										gap: 2
									}}
								>
									{items.map((item, index) => (
										<Card key={item.id} variant="outlined">
											<CardContent>
												<Typography
													variant="subtitle2"
													sx={{
														fontWeight: 600,
														mb: 0,
														display: "flex",
														alignItems: "center",
														gap: 0.75
													}}
												>
													<SchoolIcon fontSize="small" />
													{`${index + 1}. ${item.title || ""}`.trim()}
												</Typography>
                                                <StripedGuide />
												{item.photoUrl && (
													<CardMedia
														component="img"
														image={item.photoUrl}
														alt={item.title || "Foto"}
													/>
												)}
											</CardContent>
										</Card>
									))}
								</Box>
							</CardContent>
						</Card>
					);
				})}
			</Box>
		</Container>
	);
};

export default HandysSchuleCollections;
